/**
 * The Work board: read-only (spec 2026-10-28, "Client Work view shows the
 * portal lifecycle read-only"). It reads `GET /api/work` - the brand's own
 * work items, briefed and briefless alike, grouped by `ClientPhase` - and
 * nothing else. No drag, no column reorder, no editable field, no comment
 * box: a client comments on their Trello card directly (routed to the editor
 * by the n8n comment ping) and gives revision feedback in FreeFrame.
 *
 * @module web/screens/app/board/BoardScreen
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { get } from "../../../api/client.ts";
import type { ClientWorkItem, ClientWorkResponse } from "../../../api/types.ts";
import { useBrand } from "../../../state/BrandContext.tsx";
import { avatarColor, avatarInitial, formatDue } from "./cards.ts";
import { CardPanel } from "./CardPanel.tsx";
import { groupByPhase, type PhaseColumn } from "./phases.ts";
import { REFRESH_MS, shouldAutoRefresh } from "./refresh.ts";

const SKELETON_COLUMNS = 3;

export function BoardScreen({ initialCardId = null }: {
  /** `#work/board/<workItemId>`: once the board is loaded, open this item's panel. */
  initialCardId?: string | null;
}) {
  const { notify } = useBrand();
  const [items, setItems] = useState<ClientWorkItem[] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const [openId, setOpenId] = useState<string | null>(null);

  /**
   * Read the board. `quiet` skips the skeleton: the auto-refresh re-reads every
   * few seconds and flashing the board to a loading state each time would be
   * worse than being a few seconds stale. A quiet read that FAILS also stays
   * quiet - the copy on screen is the last one the server agreed with.
   */
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState("loading");
    try {
      const data = await get<ClientWorkResponse>("/api/work");
      setItems(data.items ?? []);
      setState("ready");
    } catch {
      if (!quiet) setState("failed");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const tick = () => {
      if (!shouldAutoRefresh({
        visible: document.visibilityState === "visible",
        ready: state === "ready",
        panelOpen: openId !== null,
      })) return;
      void load(true);
    };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [load, state, openId]);

  // The deep link from a Work card: open its panel once the board is here.
  const deepLinked = useRef<string | null>(null);
  useEffect(() => {
    if (!initialCardId || state !== "ready" || deepLinked.current === initialCardId) return;
    deepLinked.current = initialCardId;
    if (!items?.some(i => i.id === initialCardId)) {
      notify("That card isn't on the board right now.");
      return;
    }
    setOpenId(initialCardId);
  }, [initialCardId, state, items, notify]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpenId(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const columns = items ? groupByPhase(items) : [];
  const openItem = items?.find(i => i.id === openId) ?? null;

  return (
    <div className="board-screen">
      <div className="board-header fade-in">
        <div>
          <h1 id="board-title">Work</h1>
        </div>
        <button className="board-refresh-btn" onClick={() => void load()} title="Refresh board" aria-label="Refresh board">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
          </svg>
        </button>
      </div>

      <div className="kanban-wrap" id="kanban-wrap">
        {state === "failed"
          ? (
            <div style={{ padding: 40, color: "var(--text-2)", fontSize: 14, textAlign: "center" }}>
              Your work is temporarily unavailable. Nothing is lost - try again in a moment.
            </div>
          )
          : state === "loading"
          ? Array.from({ length: SKELETON_COLUMNS }, (_, i) => (
            <div className="kanban-col" key={i}>
              <div className="col-header"><span className="col-title">&nbsp;</span></div>
              <div className="col-cards">
                {[1, 2].map(j => <div className="kc-skeleton kc-skel-card" key={j} />)}
              </div>
            </div>
          ))
          : columns.every(c => c.items.length === 0)
            ? <BoardEmpty />
            : columns.map(col => (
              <Column key={col.phase} col={col} onOpen={setOpenId} />
            ))}
      </div>

      <CardPanel item={openItem} onClose={() => setOpenId(null)} />
    </div>
  );
}

function Column({ col, onOpen }: { col: PhaseColumn; onOpen: (id: string) => void }) {
  return (
    <div className="kanban-col" data-phase={col.phase} aria-label={`${col.label} column`}>
      <div className="col-header">
        <span className="col-title">{col.label}</span>
        <span className="col-count">{col.items.length}</span>
      </div>
      <div className="col-cards">
        {col.items.length === 0
          ? (
            <div className="empty-col">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              Nothing here
            </div>
          )
          : col.items.map((item, i) => <Card item={item} index={i} key={item.id} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

function Card({ item, index, onOpen }: { item: ClientWorkItem; index: number; onOpen: (id: string) => void }) {
  const due = formatDue(item.deadlineAt, item.phase);
  const title = item.title || "(Untitled)";

  return (
    <div
      className="kanban-card kc-enter"
      role="button"
      tabIndex={0}
      aria-label={title}
      data-work-item-id={item.id}
      style={{ animationDelay: `${Math.min(index * 30, 600)}ms` }}
      onClick={() => onOpen(item.id)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(item.id); }
      }}
    >
      <div className="kc-title">{title}</div>
      <div className="kc-meta">
        {item.editorFirstName
          ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span className="kc-avatar" aria-hidden="true" style={{ width: 18, height: 18, fontSize: 9, background: avatarColor(item.editorFirstName) }}>
                {avatarInitial(item.editorFirstName)}
              </span>
              <span className="editor">{item.editorFirstName}</span>
            </span>
          )
          : <span className="editor unassigned">unassigned</span>}
        {due && <span className={`kc-due${due.overdue ? " overdue" : ""}`}>{due.label}</span>}
      </div>
    </div>
  );
}

function BoardEmpty() {
  return (
    <div style={{ padding: 40, color: "var(--text-2)", fontSize: 14, textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
      <p className="t-body">
        No briefings on the board yet. Send your first brief and it shows up
        here, from "briefing received" to delivered.
      </p>
    </div>
  );
}
